import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useAcknowledgeAlert } from "../api/hooks/useAcknowledgeAlert";
import { useAlerts } from "../api/hooks/useAlerts";
import type { components } from "../api/schema";

type AlertResponse = components["schemas"]["AlertResponse"];
type Filter = "all" | "pending";

function formatDateTime(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return `${dt.toISOString().slice(0, 10)} ${dt.toISOString().slice(11, 16)}Z`;
}

function AlertRow({
  alert,
  onAcknowledge,
  isMutating,
}: {
  alert: AlertResponse;
  onAcknowledge: (alert: AlertResponse) => void;
  isMutating: boolean;
}) {
  return (
    <article
      data-testid={`alert-row-${alert.alert_id}`}
      data-acknowledged={alert.is_acknowledged ? "true" : undefined}
      className={
        "rounded-control border border-outline-variant bg-surface-container p-4 " +
        (alert.is_acknowledged ? "opacity-60" : "")
      }
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={
              "rounded px-2 py-0.5 text-xs font-medium " +
              (alert.is_acknowledged
                ? "bg-surface-container-high text-on-surface-variant"
                : "bg-primary text-on-primary")
            }
          >
            {alert.is_acknowledged ? "seen" : "new"}
          </span>
          <Link
            to="/trades/$tradeId"
            params={{ tradeId: alert.trade_id }}
            search={{ from: "alerts" }}
            className="text-sm font-medium text-primary underline"
          >
            Trade {alert.trade_id.slice(0, 8)}
          </Link>
        </div>
        <span className="text-xs text-on-surface-variant">
          Fired {formatDateTime(alert.fired_at)}
        </span>
      </header>
      <p className="mt-1 text-xs text-on-surface-variant">
        Rule {alert.alert_rule_id.slice(0, 8)}
      </p>
      <footer className="mt-3 flex justify-end gap-2">
        {alert.is_acknowledged ? null : (
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onAcknowledge(alert)}
            className="rounded-control border border-outline-variant bg-surface px-3 py-1 text-sm text-on-surface transition-colors hover:bg-surface-container-high"
          >
            Mark seen
          </button>
        )}
      </footer>
    </article>
  );
}

export function AlertsPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const alertsQuery = useAlerts({
    include_acknowledged: filter === "all",
  });
  const acknowledgeMutation = useAcknowledgeAlert();
  const items = alertsQuery.data?.items ?? [];

  return (
    <section className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Alerts
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Rule-driven inbox. Each entry is a single trade close that
            matched a rule's criteria.
          </p>
        </div>
        <Link
          to="/alerts/rules"
          className="rounded-control border border-outline-variant bg-surface px-3 py-1.5 text-sm font-medium text-on-surface hover:bg-surface-container-high"
        >
          Manage rules
        </Link>
      </header>

      <div
        role="radiogroup"
        aria-label="Filter alerts"
        className="flex gap-1"
      >
        {(["all", "pending"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={filter === value}
            data-testid={`alerts-filter-${value}`}
            onClick={() => setFilter(value)}
            className={
              "rounded-control border px-3 py-1 text-sm " +
              (filter === value
                ? "border-primary bg-primary text-on-primary"
                : "border-outline-variant bg-surface text-on-surface hover:bg-surface-container-high")
            }
          >
            {value === "all" ? "All" : "Pending"}
          </button>
        ))}
      </div>

      {alertsQuery.isLoading ? (
        <p role="status" aria-live="polite" className="text-on-surface-variant">
          Loading alerts…
        </p>
      ) : alertsQuery.isError ? (
        <p role="alert" className="text-sm font-medium text-danger">
          Failed to load alerts.
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-on-surface-variant">
          {filter === "pending"
            ? "No pending alerts."
            : "No alerts yet. Once a trade close matches a rule's criteria, it lands here."}
        </p>
      ) : (
        <ul data-testid="alerts-list" className="space-y-3">
          {items.map((alert) => (
            <li key={alert.alert_id}>
              <AlertRow
                alert={alert}
                isMutating={acknowledgeMutation.isPending}
                onAcknowledge={(target) =>
                  acknowledgeMutation.mutate(target.alert_id)
                }
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
